<script setup>
import WrTag from "~/components/global/WrTag.vue";

const route = useRoute();
const slug = route.params.slug;
const config = useRuntimeConfig();
const baseURL = config.public.baseUrl;
const { data, error } = await useFetch(
  `${baseURL}/content/case-studies/${slug}-content.json`
);

if (error.value || !data.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page not found",
  });
}

useHead({
  title: data?.value?.meta_tags?.title || "",
  meta: data?.value?.meta_tags?.meta || [],
});

const caseStudyAboutTechnology = computed(() => {
  return data.value.case_study_about_project.technology.split(", ");
});
const images = ref([]);

onMounted(() => {
  const imgArray = data.value.case_study_features_project.portfolio_images;

  images.value = imgArray.map((src, index) => ({
    itemImageSrc: src.image,
    alt: `Image ${index + 1}`,
  }));
});
</script>

<template>
  <section class="text-center pb-16 px-4">
    <h1 v-if="data?.header?.title" class="font-bold">
      <WrGradientText> {{ data.header.title }} </WrGradientText>
    </h1>
    <p v-if="data?.header?.text" class="text-lg tracking-[0.5px]">
      {{ data.header.text }}
    </p>

    <!-- Main Section -->
    <div
      v-if="data?.case_study_about_project"
      class="mt-12 grid md:grid-cols-2 items-start max-w-screen-xl mx-auto"
    >
      <NuxtImg
        :src="data.case_study_about_project.image"
        :alt="data.case_study_about_project.alt"
        width="565"
        height="360"
        loading="lazy"
        format="webp"
        class="max-md:mb-6 max-md:block max-md:mx-auto"
      />

      <div class="text-left">
        <p class="!leading-[1.8] tracking-[0.5px]">
          {{ data.case_study_about_project.text_1 }}
        </p>
        <h3 class="mt-6 font-semibold text-lg text-[#2D1040] dark:text-inherit">
          Tools and Technology
        </h3>
        <div class="flex flex-wrap gap-2 mt-2">
          <div
            v-for="language in caseStudyAboutTechnology"
            class="dark:block dark:bg-[linear-gradient(91.95deg,_#51277B_4.5%,_#8F5BC4_96.91%)] shadow-[1px_2px_5px_0px_#00000026] rounded-full max-w-max"
          >
            <WrTag :tag="language" />
          </div>
        </div>
      </div>
    </div>

    <!-- Project Brief -->
    <div
      v-if="data?.case_study_brief_project"
      class="mt-20 max-w-screen-xl mx-auto text-left"
    >
      <h2 class="font-bold">
        <WrGradientText> Project Brief </WrGradientText>
      </h2>
      <p class="!leading-[1.8] tracking-[0.5px]">
        <br /><br />
        {{ data.case_study_brief_project.description_1 }}
      </p>
       <p class="!leading-[1.8] tracking-[0.5px]">
        <br /><br />
        {{ data.case_study_brief_project.description_2 }}
      </p>
    </div>

    <!-- Important Features -->
    <div
      class="mt-20 max-w-screen-xl mx-auto grid md:grid-cols-2 gap-8 text-left"
    >
      <div>
        <h2 class="font-bold">
          <WrGradientText> Important Features </WrGradientText>
        </h2>
        <div
          v-if="data?.case_study_features_project?.description"
          class="!leading-[1.8] tracking-[0.5px]"
          v-html="data.case_study_features_project.description"
        ></div>
      </div>
      <div>
        <div class="mx-auto">
          <Galleria
            :value="images"
            :numVisible="5"
            containerStyle="max-width: 610px"
            :showThumbnails="false"
            :showItemNavigators="true"
            :showIndicators="false"
            :pt="{
              root: { class: 'group border-0 ml-auto rounded-br-none' },
              nextButton: {
                class:
                  '!bg-white !text-gray-500 rounded-none hover:border border-[#5f4ca4] opacity-0 group-hover:opacity-100 transition-opacity duration-200',
              },
              prevButton: {
                class:
                  '!bg-white !text-gray-500 !rounded-none hover:border !border-black opacity-0 group-hover:opacity-100 transition-opacity duration-200',
              },
            }"
          >
            <template #item="slotProps">
              <NuxtImg
                :src="slotProps.item.itemImageSrc"
                :alt="slotProps.item.alt"
                width="565"
                height="568"
                style="width: 100%; display: block"
                loading="lazy"
                format="webp"
              />
            </template>
          </Galleria>
          <div
            class="px-5 pt-9 pb-4 bg-[#5f4ca4] text-white rounded-b-md ml-10 lg:ml-24 relative mt-0"
          >
            <NuxtImg
              src="/images/icons/quote.webp"
              alt="Quote"
              width="64"
              height="64"
              class="absolute left-6 -top-8 [filter:drop-shadow(5px_5px_2px_rgba(0,0,0,0.25))]"
              loading="lazy"
              format="webp"
            />
           <figure class="max-w-xl mx-auto p-6 rounded-2xl">
              <blockquote class="text-lg italic">
           {{ data.case_study_features_project.testimonial }}
              </blockquote>
              <figcaption class="mt-4 text-right text-sm ">
            — {{ data.case_study_features_project.author_name }}
              </figcaption>
          </figure>
          </div>
        </div>

        <!-- <NuxtImg src="/images/in-01.webp" alt="App Screenshot" width="565" height="568" class="ml-auto" /> -->
      </div>
    </div>
  </section>

  <section
    v-if="data?.case_study_features_admin"
    class="py-12 px-4 max-w-7xl mx-auto lg:text-center"
  >
    <h2 class="font-bold">
      <WrGradientText>
        {{ data?.case_study_features_admin?.heading }}
      </WrGradientText>
    </h2>
    <p class="tracking-[0.5px] mb-10 mx-auto">
      {{ data?.case_study_features_admin?.subheading }}
    </p>

    <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <CardFunctionality
        v-for="(item, i) in data?.case_study_features_admin
          ?.admin_icon_card_content"
        :key="i"
        :img="item.image"
        :title="item.heading"
        :description="item.subheading"
      />
    </div>
  </section>

  <section
    v-if="data?.case_study_features_user"
    class="py-12 px-4 max-w-7xl mx-auto lg:text-center"
  >
    <h2 class="font-bold">
      <WrGradientText>
        {{ data.case_study_features_user.heading }}
      </WrGradientText>
    </h2>
    <p class="tracking-[0.5px] mb-10 mx-auto">
      {{ data.case_study_features_user.subheading }}
    </p>

    <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <CardFunctionality
        v-for="(item, i) in data.case_study_features_user
          .user_icon_card_content"
        :key="i"
        :img="item.image"
        :title="item.heading"
        :description="item.subheading"
      />
    </div>
  </section>

  <section
    v-if="data?.case_study_features_third_party_integration"
    class="py-12 px-4 max-w-7xl mx-auto lg:text-center"
  >
    <h2 class="font-bold">
      <WrGradientText>
        {{ data.case_study_features_third_party_integration.heading }}
      </WrGradientText>
    </h2>
    <p class="tracking-[0.5px] mb-10 mx-auto">
      {{ data.case_study_features_third_party_integration.subheading }}
    </p>

    <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <CardFunctionality
        v-for="(item, i) in data.case_study_features_third_party_integration
          .tp_icon_card_content"
        :key="i"
        :img="item.image"
        :title="item.heading"
        :description="item.subheading"
      />
    </div>
  </section>
</template>
